/**
 * @file Builds the header and footer parameters for a PDF export and triggers
 * the export itself. Consumed by `FormExportModal` (Preview PDF) and, through
 * `usePdfAttachment`, by `FormEmailModal` and `WorkflowParameterContent` when a
 * send includes a PDF attachment. Invariant: `paramsError` reports only a
 * failure to load the organization parameters, because all three consumers read
 * it as a hard gate. The header logo is decorative: with no `logoUrl`, or a
 * remote one that local dev skips, the bundled Syncretic logo stands in; a logo
 * that cannot be fetched is logged and the export runs with no logo.
 */
import { useState, useEffect, useCallback } from 'react';
import { useOrganizationParameters } from '../../../use/useOrganizationParameters';
import { pdfExport } from '../services/pdfExport';
import type { PdfExportParams } from '../services/pdfExport';
import { deploymentEnvironment, environmentType } from '../../../config';
import { LOGO_EXPORT_SIZE_DEFAULT } from '#types/System.Parameters.types';

type ExportDocumentParams = Pick<PdfExportParams, 'contentRef' | 'outputType' | 'filename'>;

const SYNCRETIC_LOGO_URL = '/art/Syncretic-logo-horizontal@3x.png';

/** Represents the data needed to configure PDF export headers and footers. */
export interface PdfExportConfigParams {
    formNumber?: string;
}

/** Represents the parameters returned by the usePdfExport hook. */
export interface PdfExportResult {
    /** Base64 Data URI string for the header logo. Null if loading or error. */
    headerLogoDataUrl: string | null;
    /** Text for the right side of the header. */
    headerRightText: string;
    /** Text lines for the left side of the footer. Undefined if no text. */
    footerLeftText: string[] | undefined;
    /** Indicates if the *parameters* (logo, org data) are currently loading. */
    isLoadingParams: boolean;
    /** Stores any error message encountered while loading the organization parameters. Null when only the logo could not be fetched. */
    paramsError: string | null;
    /** Indicates if the PDF *export process* is currently running. */
    isExporting: boolean;
    /** Stores any error message encountered during the *PDF export process*. */
    exportError: string | null;
    /** Function to trigger the PDF export or preview generation. */
    exportDocument: (params: ExportDocumentParams) => Promise<Blob | string | void>;
}

/**
 * Hook to generate dynamic parameters and provide a function to trigger PDF export.
 * @param configParams - Configuration data, like the form number, needed to generate parameters.
 * @returns An object containing the generated PDF parameters, loading/error states for params and export, and the export trigger function.
 */
export const usePdfExport = (configParams: PdfExportConfigParams): PdfExportResult => {
    const { data: orgParams, loading: isOrgParamsLoading, error: orgParamsErrorHook } = useOrganizationParameters();

    const [logoDataUri, setLogoDataUri] = useState<string | null>(null);
    const [isLogoLoading, setIsLogoLoading] = useState<boolean>(true);

    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [exportError, setExportError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;

        const isLocalDev = environmentType === 'development' && deploymentEnvironment === 'local';

        const isRemoteHttpUrl = (url: string) => {
            try {
                const parsed = new URL(url);
                return parsed.protocol === 'http:' || parsed.protocol === 'https:';
            } catch {
                return false;
            }
        };

        const isLocalhostHttpUrl = (url: string) => {
            try {
                const parsed = new URL(url);
                return parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
            } catch {
                return false;
            }
        };

        const readBlobAsDataUrl = (blob: Blob) => new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error("Failed to read logo blob."));
            reader.readAsDataURL(blob);
        });

        const fetchLogoDataUrl = async (logoUrl: string): Promise<string> => {
            if (logoUrl.startsWith('data:')) {
                return logoUrl;
            }

            const response = await fetch(logoUrl);
            if (!response.ok && response.status !== 304) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return readBlobAsDataUrl(await response.blob());
        };

        const organizationLogoUrl = orgParams?.logoUrl;
        const resolvedLogoUrl =
            !organizationLogoUrl ||
            (isLocalDev && isRemoteHttpUrl(organizationLogoUrl) && !isLocalhostHttpUrl(organizationLogoUrl))
                ? SYNCRETIC_LOGO_URL
                : organizationLogoUrl;

        if (isOrgParamsLoading) {
            setIsLogoLoading(true);
        } else if (orgParamsErrorHook) {
            setIsLogoLoading(false);
        } else {
            setIsLogoLoading(true);
            fetchLogoDataUrl(resolvedLogoUrl).then(
                (dataUrl) => {
                    if (isMounted) {
                        setLogoDataUri(dataUrl);
                        setIsLogoLoading(false);
                    }
                },
                (fetchError: unknown) => {
                    console.warn(`Could not load logo from ${resolvedLogoUrl}; exporting without a logo:`, fetchError);
                    if (isMounted) {
                        setLogoDataUri(null);
                        setIsLogoLoading(false);
                    }
                },
            );
        }

        return () => {
            isMounted = false;
        };
    }, [isOrgParamsLoading, orgParams?.logoUrl, orgParamsErrorHook]);

    const headerRightText = configParams.formNumber ? `Form ${configParams.formNumber}` : 'Form Export';

    const footerLeftItems = [
        orgParams?.name,
        orgParams?.email && orgParams?.phone
            ? `${orgParams.email} | ${orgParams.phone}`
            : orgParams?.email || orgParams?.phone
    ].filter(Boolean);

    const footerLeftText = footerLeftItems.length > 0 ?
        footerLeftItems as string[] :
        undefined;

    const headerLogoSize = `${orgParams?.logoExportSize ?? LOGO_EXPORT_SIZE_DEFAULT}mm`;

    const isLoadingParams = isOrgParamsLoading || isLogoLoading;
    const paramsError = orgParamsErrorHook ? `Failed to load organization parameters: ${orgParamsErrorHook.message}` : null;

    const exportDocument = useCallback(async (
        { contentRef, outputType = 'save', filename }: ExportDocumentParams
    ): Promise<Blob | string | void> => {
        if (isOrgParamsLoading || orgParamsErrorHook) {
            const errorMsg =
                orgParamsErrorHook
                    ? `Failed to load organization parameters: ${orgParamsErrorHook.message}`
                    : "Parameters are still loading. Cannot export.";
            console.error("Export cancelled:", errorMsg);
            setExportError(errorMsg);
            throw new Error(errorMsg);
        }

        setIsExporting(true);
        setExportError(null);

        try {
            const result = await pdfExport({
                contentRef,
                outputType,
                headerLogoDataUrl: logoDataUri || undefined,
                headerLogoSize,
                headerRightText,
                footerLeftText,
                filename,
            });
            setIsExporting(false);
            return result;
        } catch (err) {
            console.error("PDF Export failed inside hook:", err);
            const errorMsg = `PDF export failed: ${err instanceof Error ? err.message : String(err)}`;
            setExportError(errorMsg);
            setIsExporting(false);
            throw err;
        }
    }, [
        isOrgParamsLoading,
        orgParamsErrorHook,
        logoDataUri,
        headerLogoSize,
        headerRightText,
        footerLeftText,
    ]);

    return {
        headerLogoDataUrl: logoDataUri,
        headerRightText,
        footerLeftText,
        isLoadingParams,
        paramsError,
        isExporting,
        exportError,
        exportDocument,
    };
}; 